import { useEffect, useState, useCallback } from 'react';
import { Wifi, WifiOff, RefreshCw, AlertTriangle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { addSyncListener, performFullSync, getCachedBranchName, syncPendingProducts, syncPendingPurchases, syncPendingAdrReports } from '../lib/syncService';
import { getPendingSales, getPendingProducts, getPendingPurchases, getPendingAdrReports, getAllOfflineProducts, getAllOfflinePurchases, getAllOfflineAdrReports } from '../lib/offlineDB';
import api from '../lib/api';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from './ui/dropdown-menu';
import SyncConflictsDialog from './SyncConflictsDialog';
import OfflineFailuresDialog from './OfflineFailuresDialog';

/**
 * Persistent, unobtrusive connection/sync indicator for the DutiLyne Desktop
 * Terminal (and the regular web app, where it's just always "Online").
 * States: Online / Working offline / Syncing / Sync issue — framed neutrally,
 * never as an error, with a "Sync now" action and a minimal conflicts review.
 */
export default function ConnectionStatusChip() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [syncIssue, setSyncIssue] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [showConflicts, setShowConflicts] = useState(false);
  const [showFailures, setShowFailures] = useState(false);
  const [cachedBranchName, setCachedBranchName] = useState(null);

  const refreshPending = useCallback(async () => {
    try {
      const [sales, products, purchases, adrReports] = await Promise.all([
        getPendingSales(), getPendingProducts(), getPendingPurchases(), getPendingAdrReports(),
      ]);
      setPendingCount(sales.length + products.length + purchases.length + adrReports.length);
      const [allProducts, allPurchases, allAdr] = await Promise.all([
        getAllOfflineProducts(), getAllOfflinePurchases(), getAllOfflineAdrReports(),
      ]);
      setFailedCount(
        allProducts.filter((r) => r.status === 'failed').length
        + allPurchases.filter((r) => r.status === 'failed').length
        + allAdr.filter((r) => r.status === 'failed').length
      );
    } catch (e) { console.warn('Pending queue count unavailable:', e); }
  }, []);

  const refreshConflicts = useCallback(async () => {
    if (!navigator.onLine) return;
    try {
      const res = await api.get('/sync/conflicts');
      setConflicts(res.data.conflicts || []);
    } catch (e) { console.warn('Sync conflicts check failed (non-fatal, best-effort):', e); }
  }, []);

  useEffect(() => {
    refreshPending();
    refreshConflicts();
    getCachedBranchName().then(setCachedBranchName).catch(() => {});

    const unsub = addSyncListener((event, data) => {
      if (event === 'sales_sync_start' || event === 'full_sync_start') setSyncing(true);
      if (event === 'sales_sync_complete' || event === 'full_sync_complete') {
        setSyncing(false);
        setSyncIssue(!!data?.failed);
        refreshPending();
        refreshConflicts();
        getCachedBranchName().then(setCachedBranchName).catch(() => {});
      }
      if (['products_queue_synced', 'purchases_queue_synced', 'adr_reports_queue_synced'].includes(event)) refreshPending();
      if (event === 'sales_sync_error' || event === 'full_sync_error') { setSyncing(false); setSyncIssue(true); }
      if (event === 'online') { setIsOnline(true); refreshConflicts(); }
      if (event === 'offline') setIsOnline(false);
    });
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    const poll = setInterval(refreshPending, 15000);

    return () => {
      unsub();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(poll);
    };
  }, [refreshPending, refreshConflicts]);

  const handleSyncNow = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const result = await performFullSync();
      const [products, purchases, adrReports] = await Promise.all([
        syncPendingProducts(), syncPendingPurchases(), syncPendingAdrReports(),
      ]);
      const synced = (result.sales?.synced || 0) + (products.synced || 0) + (purchases.synced || 0) + (adrReports.synced || 0);
      toast.success(synced > 0 ? `Synced ${synced} offline record(s)` : 'Already up to date');
      setSyncIssue(!!result.sales?.failed || !!products.failed || !!purchases.failed || !!adrReports.failed);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || 'Sync failed — will retry automatically');
      setSyncIssue(true);
    } finally {
      setSyncing(false);
      refreshPending();
      refreshConflicts();
    }
  };

  let tone = 'bg-emerald-100 text-emerald-800 border-emerald-300';
  let Icon = Wifi;
  let spin = false;
  let label = 'Online';

  if (syncing) {
    tone = 'bg-sky-100 text-sky-800 border-sky-300';
    Icon = RefreshCw; spin = true; label = 'Syncing…';
  } else if (!isOnline) {
    tone = 'bg-slate-100 text-slate-700 border-slate-300';
    Icon = WifiOff;
    label = pendingCount > 0 ? `Working offline · ${pendingCount} queued` : 'Working offline';
  } else if (syncIssue || conflicts.length > 0 || failedCount > 0) {
    tone = 'bg-amber-100 text-amber-800 border-amber-300';
    Icon = AlertTriangle;
    const toReview = conflicts.length + failedCount;
    label = toReview > 0 ? `${toReview} to review` : 'Sync issue';
  } else if (pendingCount > 0) {
    tone = 'bg-sky-100 text-sky-800 border-sky-300';
    Icon = RefreshCw;
    label = `${pendingCount} queued`;
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-testid="connection-status-chip"
            className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors select-none ${tone}`}
            title={isOnline ? 'Connected to DutiLyne cloud' : 'Working offline — will sync automatically'}
          >
            <Icon className={`h-3 w-3 ${spin ? 'animate-spin' : ''}`} />
            <span>{label}</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            {isOnline ? 'Connected to DutiLyne cloud' : 'Working offline — will sync automatically'}
          </DropdownMenuLabel>
          {cachedBranchName && (
            <DropdownMenuLabel className="text-[11px] font-normal text-muted-foreground -mt-2 pt-0" data-testid="offline-cached-branch">
              Offline data cached for: <span className="font-medium">{cachedBranchName}</span>
            </DropdownMenuLabel>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="sync-now-btn" onClick={handleSyncNow} disabled={syncing || !isOnline}>
            <RefreshCw className={`h-3.5 w-3.5 mr-2 ${syncing ? 'animate-spin' : ''}`} /> Sync now
          </DropdownMenuItem>
          {conflicts.length > 0 && (
            <DropdownMenuItem data-testid="view-sync-issues-btn" onClick={() => setShowConflicts(true)}>
              <AlertTriangle className="h-3.5 w-3.5 mr-2 text-amber-600" /> Review {conflicts.length} sync issue(s)
            </DropdownMenuItem>
          )}
          {failedCount > 0 && (
            <DropdownMenuItem data-testid="view-offline-failures-btn" onClick={() => setShowFailures(true)}>
              <XCircle className="h-3.5 w-3.5 mr-2 text-red-600" /> Review {failedCount} failed item(s)
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <SyncConflictsDialog
        open={showConflicts}
        onOpenChange={setShowConflicts}
        conflicts={conflicts}
        onResolved={(saleId) => setConflicts((prev) => prev.filter((c) => c.sale_id !== saleId))}
      />
      <OfflineFailuresDialog open={showFailures} onOpenChange={setShowFailures} onChanged={refreshPending} />
    </>
  );
}
